import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Button, Stack, Chip, CircularProgress, Grid } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme/tokens';

const EASE_OUT_CUBIC = (t) => 1 - (1 - t) ** 3;

function useAnimatedScore(target, durationMs = 820) {
  const [value, setValue] = useState(0);
  const rafRef = useRef();

  useEffect(() => {
    setValue(0);
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / durationMs);
      const eased = EASE_OUT_CUBIC(t);
      setValue(Math.round(target * eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs]);

  return value;
}

function levelChipColor(levelKey) {
  if (levelKey === 'high' || levelKey === 'critical') return 'error';
  if (levelKey === 'medium') return 'warning';
  if (levelKey === 'low') return 'success';
  return 'default';
}

function riskLabel(levelKey, raw) {
  if (levelKey === 'critical') return 'Critical';
  if (!raw) return 'Unknown';
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

/**
 * Dashboard risk score presentation: circular gauge, badge, refresh (logic stays in parent).
 */
export default function DashboardRiskHero({ score = 0, level, onRefresh, refreshing = false, disabled = false }) {
  const theme = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const numericTarget = typeof score === 'number' ? score : Number(score) || 0;
  const clampedTarget = Math.max(0, Math.min(100, numericTarget));
  const animated = useAnimatedScore(clampedTarget);

  const levelKey = (level || '').toLowerCase();
  const chipColor = levelChipColor(levelKey);
  const label = riskLabel(levelKey, level);

  const gaugeColor =
    levelKey === 'high' || levelKey === 'critical'
      ? theme.palette.error.main
      : levelKey === 'medium'
        ? theme.palette.warning.main
        : levelKey === 'low'
          ? theme.palette.success.main
          : theme.palette.primary.main;

  const size = 176;
  const thickness = 4.2;
  const trackColor = tokens.border.strong;

  const chipProps =
    chipColor === 'default'
      ? {
          label,
          variant: 'outlined',
          size: 'medium',
          sx: {
            fontWeight: 600,
            borderColor: tokens.border.strong,
            color: tokens.text.secondary,
            bgcolor: 'rgba(148, 163, 184, 0.06)',
          },
        }
      : {
          label,
          color: chipColor,
          variant: 'outlined',
          size: 'medium',
          sx: { fontWeight: 600 },
        };

  return (
    <Box
      sx={{
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(10px)',
        transition: 'opacity 0.45s ease, transform 0.45s ease',
      }}
    >
      <Grid container spacing={{ xs: 2.5, md: 3 }} alignItems="center">
        <Grid item xs={12} md="auto" sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
          <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
            <CircularProgress
              variant="determinate"
              value={100}
              size={size}
              thickness={thickness}
              sx={{
                color: trackColor,
                position: 'absolute',
                left: 0,
                top: 0,
              }}
            />
            <CircularProgress
              variant="determinate"
              value={animated}
              size={size}
              thickness={thickness}
              sx={{
                color: gaugeColor,
                position: 'absolute',
                left: 0,
                top: 0,
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 800, color: gaugeColor, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {animated}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.06em' }}>
                / 100
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md sx={{ minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 1.25, lineHeight: 1.25 }}>
            Privacy Risk Score
          </Typography>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            justifyContent={{ xs: 'center', md: 'flex-start' }}
          >
            <Chip {...chipProps} />
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, lineHeight: 1.55 }}>
              Based on unresolved exposures and sensitivity of detected data.
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={12} md="auto" sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<RefreshIcon />}
            disabled={disabled || refreshing}
            onClick={onRefresh}
            sx={{ minWidth: 148, alignSelf: { xs: 'stretch', md: 'center' } }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
